import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton, TableSortLabel,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment,
  Button, Menu, ListItemText, Divider, Checkbox, ListItemIcon,
} from '@mui/material';
import {
  Search, ChevronLeft, ChevronRight, Visibility, CheckCircle, Cancel,
  PictureAsPdf, ArrowDropDown, Email,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import apiClient from '../api/client';
import { Timesheet, TimesheetEntry, Client } from '../types';
import TimesheetGrid from '../components/timesheet/TimesheetGrid';
import generateTimesheetPDF from '../utils/generateTimesheetPDF';
import { parseLocalDate, formatHoursMinutes } from '../utils/dateUtils';
import { DateRangeValidation, validateDateRangeEntries } from '../utils/timesheetDateRangeValidation';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type SortField = 'employee_name' | 'client_name' | 'status' | 'total_hours' | 'updated_at' | 'hourly_rate';
type SortDir = 'asc' | 'desc';

const statusDisplayLabels: Record<string, string> = {
  draft: 'Billable',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
};

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function AdminTimesheetsPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState(() => searchParams.get('uid') || '');
  const [clientFilter, setClientFilter] = useState<string[]>([]);

  // Clear uid param from URL after reading it (keep URL clean)
  useEffect(() => {
    if (searchParams.get('uid')) {
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorting
  const [sortField, setSortField] = useState<SortField>('employee_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // View dialog
  const [viewTs, setViewTs] = useState<Timesheet | null>(null);
  const [viewEntries, setViewEntries] = useState<TimesheetEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Approve/Reject dialogs
  const [approveTarget, setApproveTarget] = useState<Timesheet | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Timesheet | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Download menu
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);

  // Email menu
  const [emailMenuAnchor, setEmailMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [pendingEmailFilter, setPendingEmailFilter] = useState<[number, number] | undefined>(undefined);
  const [pendingEmailDateRange, setPendingEmailDateRange] = useState<{ from: string; to: string } | undefined>(undefined);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string>('');

  // Date range dialog (shared for download + email)
  const [dateRangeTarget, setDateRangeTarget] = useState<Timesheet | null>(null);
  const [dateRangeEmailMode, setDateRangeEmailMode] = useState(false);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [dateRangeLoading, setDateRangeLoading] = useState(false);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [dateRangePreview, setDateRangePreview] = useState<{entries: any[], employee: any} | null>(null);
  const [dateRangeValidation, setDateRangeValidation] = useState<DateRangeValidation | null>(null);
  const [dateRangeFetching, setDateRangeFetching] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Fetch timesheets & clients
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { month, year };

      const [tsRes, clientRes] = await Promise.all([
        apiClient.get('/timesheets', { params }),
        apiClient.get('/clients'),
      ]);
      setTimesheets(tsRes.data);
      setClients(clientRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Month navigation
  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Client-side filtering (search + sorting)
  const filtered = useMemo(() => {
    let result = [...timesheets];

    // Client filter
    if (clientFilter.length > 0) {
      result = result.filter(ts => clientFilter.includes(ts.client_id));
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(ts =>
        ts.employee_name.toLowerCase().includes(q) ||
        ts.employee_uid.toLowerCase().includes(q) ||
        ts.client_name.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortField === 'total_hours') {
        aVal = Number(a.total_hours || 0);
        bVal = Number(b.total_hours || 0);
        return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }

      if (sortField === 'updated_at') {
        aVal = a.updated_at || '';
        bVal = b.updated_at || '';
      } else {
        aVal = (a[sortField] ?? '').toString().toLowerCase();
        bVal = (b[sortField] ?? '').toString().toLowerCase();
      }

      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [timesheets, search, clientFilter, sortField, sortDir]);

  // View timesheet entries
  const handleView = async (ts: Timesheet) => {
    setViewTs(ts);
    setLoadingEntries(true);
    try {
      const { data } = await apiClient.get(`/timesheets/${ts.id}/entries`);
      // Strict month filter — never show entries outside the timesheet's month
      const filtered = data.filter((e: TimesheetEntry) => {
        const d = parseLocalDate(e.entry_date);
        return d.getMonth() + 1 === ts.month && d.getFullYear() === ts.year;
      });
      setViewEntries(filtered);
    } catch (err) {
      console.error('Fetch entries error:', err);
      setSnackbar({ message: 'Failed to load entries', severity: 'error' });
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleCloseView = () => {
    setViewTs(null);
    setViewEntries([]);
    setDownloadMenuAnchor(null);
    setEmailMenuAnchor(null);
  };

  // Email menu select
  const handleEmailMenuSelect = (weekFilter?: [number, number]) => {
    setEmailMenuAnchor(null);
    setPendingEmailFilter(weekFilter);
    setPendingEmailDateRange(undefined);
    setEmailConfirmOpen(true);
  };

  // Build email filter label for confirmation dialog
  const getEmailFilterLabel = () => {
    if (!viewTs) return '';
    const prefix = `${MONTH_ABBR[viewTs.month - 1]} ${viewTs.year}`;
    if (pendingEmailDateRange) {
      const fmt = (iso: string) => {
        const d = parseLocalDate(iso);
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      };
      return `${fmt(pendingEmailDateRange.from)} – ${fmt(pendingEmailDateRange.to)}`;
    }
    if (!pendingEmailFilter) return `${prefix} — Full Month`;
    if (pendingEmailFilter[0] === pendingEmailFilter[1]) {
      return `${prefix} — Week ${String(pendingEmailFilter[0]).padStart(2, '0')}`;
    }
    return `${prefix} — Week ${String(pendingEmailFilter[0]).padStart(2, '0')} & ${String(pendingEmailFilter[1]).padStart(2, '0')}`;
  };

  // Send email with PDF attachment
  const handleEmailTimesheet = async () => {
    if (!viewTs) return;
    setEmailConfirmOpen(false);
    setEmailSending(true);

    try {
      let pdfResult: { blob: Blob; filename: string } | undefined;
      let dateRangeLabel: string;

      if (pendingEmailDateRange) {
        // Custom date range — fetch entries from API
        const { data } = await apiClient.get('/reports/date-range-entries', {
          params: { employee_id: viewTs.employee_id, from: pendingEmailDateRange.from, to: pendingEmailDateRange.to },
        });
        const { employee, entries } = data;
        if (!entries || entries.length === 0) {
          setEmailError('No timesheet exists for the selected dates');
          setEmailStatus('error');
          setTimeout(() => setEmailStatus('idle'), 6000);
          setEmailSending(false);
          return;
        }
        pdfResult = generateTimesheetPDF({
          employeeName: employee.name,
          clientName: employee.client_name,
          month: parseLocalDate(pendingEmailDateRange.from).getMonth() + 1,
          year: parseLocalDate(pendingEmailDateRange.from).getFullYear(),
          status: viewTs.status,
          expectedDailyHours: Number(employee.expected_daily_hours || 8),
          totalHours: 0,
          entries,
          dateRange: pendingEmailDateRange,
          returnBlob: true,
          isIndirect: viewTs.is_indirect,
        }) as { blob: Blob; filename: string } | undefined;

        const fmt = (iso: string) => {
          const d = parseLocalDate(iso);
          return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
        };
        dateRangeLabel = `${fmt(pendingEmailDateRange.from)} – ${fmt(pendingEmailDateRange.to)}`;
      } else {
        // Week / full month — use already-loaded viewEntries
        pdfResult = generateTimesheetPDF({
          employeeName: viewTs.employee_name,
          jobTitle: viewTs.job_title,
          clientName: viewTs.client_name,
          month: viewTs.month,
          year: viewTs.year,
          status: viewTs.status,
          expectedDailyHours: Number(viewTs.expected_daily_hours || 8),
          totalHours: Number(viewTs.total_hours || 0),
          entries: viewEntries,
          weekFilter: pendingEmailFilter,
          returnBlob: true,
          isIndirect: viewTs.is_indirect,
        }) as { blob: Blob; filename: string } | undefined;

        const monthName = MONTHS[viewTs.month - 1];
        if (pendingEmailFilter) {
          if (pendingEmailFilter[0] === pendingEmailFilter[1]) {
            dateRangeLabel = `${monthName} ${viewTs.year} - Week ${String(pendingEmailFilter[0]).padStart(2, '0')}`;
          } else {
            dateRangeLabel = `${monthName} ${viewTs.year} - Week ${String(pendingEmailFilter[0]).padStart(2, '0')} & ${String(pendingEmailFilter[1]).padStart(2, '0')}`;
          }
        } else {
          dateRangeLabel = `${monthName} ${viewTs.year}`;
        }
      }

      if (!pdfResult) throw new Error('Failed to generate PDF');

      // Convert blob to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfResult!.blob);
      });

      await apiClient.post('/timesheets/email', {
        pdfBase64: base64,
        pdfFilename: pdfResult.filename,
        employeeName: viewTs.employee_name,
        clientName: viewTs.client_name,
        dateRange: dateRangeLabel,
        employeeId: viewTs.employee_id,
      });

      setEmailStatus('sent');
      setTimeout(() => setEmailStatus('idle'), 4000);
    } catch (err: any) {
      console.error('Failed to email timesheet:', err);
      const msg = err?.response?.data?.error || 'Failed to send email. Please try again.';
      setEmailError(msg);
      setEmailStatus('error');
      setTimeout(() => setEmailStatus('idle'), 6000);
    } finally {
      setEmailSending(false);
    }
  };

  // Download PDF
  const handleDownloadPDF = (weekFilter?: [number, number]) => {
    if (!viewTs) return;
    setDownloadMenuAnchor(null);
    generateTimesheetPDF({
      employeeName: viewTs.employee_name,
      clientName: viewTs.client_name,
      month: viewTs.month,
      year: viewTs.year,
      status: viewTs.status,
      expectedDailyHours: Number(viewTs.expected_daily_hours || 8),
      totalHours: Number(viewTs.total_hours || 0),
      entries: viewEntries,
      weekFilter,
      isIndirect: viewTs.is_indirect,
    });
  };

  // Approve
  const handleApprove = async () => {
    if (!approveTarget) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/timesheets/${approveTarget.id}/approve`);
      setSnackbar({ message: `Timesheet approved for ${approveTarget.employee_name}`, severity: 'success' });
      setApproveTarget(null);
      // Also close view dialog if same timesheet was open
      if (viewTs?.id === approveTarget.id) handleCloseView();
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to approve', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Reject
  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/timesheets/${rejectTarget.id}/reject`);
      setSnackbar({ message: `Timesheet rejected for ${rejectTarget.employee_name}`, severity: 'success' });
      setRejectTarget(null);
      if (viewTs?.id === rejectTarget.id) handleCloseView();
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to reject', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Date range dialog (shared for download + email)
  const handleOpenDateRange = (ts: Timesheet, emailMode = false) => {
    setDateRangeTarget(ts);
    setDateRangeEmailMode(emailMode);
    setFromDate(null);
    setToDate(null);
    setDateRangeError(null);
    setDateRangePreview(null);
    setDateRangeValidation(null);
  };

  const dateRangeTooLong = fromDate && toDate && toDate.diff(fromDate, 'day') > 93;
  const dateRangeValid = fromDate && toDate && toDate.isAfter(fromDate.subtract(1, 'day')) && !dateRangeTooLong;

  // Auto-fetch entries when both dates are valid to show validation preview
  useEffect(() => {
    if (!dateRangeTarget || !fromDate || !toDate) {
      setDateRangePreview(null);
      setDateRangeValidation(null);
      return;
    }
    const tooLong = toDate.diff(fromDate, 'day') > 93;
    if (!toDate.isAfter(fromDate.subtract(1, 'day')) || tooLong) {
      setDateRangePreview(null);
      setDateRangeValidation(null);
      return;
    }

    let cancelled = false;
    setDateRangeFetching(true);
    setDateRangeError(null);

    const fetchPreview = async () => {
      try {
        const fromStr = fromDate.format('YYYY-MM-DD');
        const toStr = toDate.format('YYYY-MM-DD');
        const { data } = await apiClient.get('/reports/date-range-entries', {
          params: { employee_id: dateRangeTarget.employee_id, from: fromStr, to: toStr },
        });
        if (cancelled) return;
        const { employee, entries } = data;
        if (!entries || entries.length === 0) {
          setDateRangePreview(null);
          setDateRangeValidation(null);
          setDateRangeError('No timesheet exists for the selected dates');
        } else {
          setDateRangePreview({ entries, employee });
          setDateRangeValidation(validateDateRangeEntries(entries, fromStr, toStr));
          setDateRangeError(null);
        }
      } catch {
        if (!cancelled) {
          setDateRangePreview(null);
          setDateRangeValidation(null);
        }
      } finally {
        if (!cancelled) setDateRangeFetching(false);
      }
    };

    fetchPreview();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeTarget?.employee_id, fromDate?.valueOf(), toDate?.valueOf()]);

  const handleDateRangeDownload = async () => {
    if (!dateRangeTarget || !fromDate || !toDate) return;
    setDateRangeLoading(true);
    setDateRangeError(null);
    try {
      const fromStr = fromDate.format('YYYY-MM-DD');
      const toStr = toDate.format('YYYY-MM-DD');

      // Use cached entries from preview, or fetch fresh
      let employee, entries;
      if (dateRangePreview) {
        employee = dateRangePreview.employee;
        entries = dateRangePreview.entries;
      } else {
        const { data } = await apiClient.get('/reports/date-range-entries', {
          params: { employee_id: dateRangeTarget.employee_id, from: fromStr, to: toStr },
        });
        employee = data.employee;
        entries = data.entries;
      }

      if (!entries || entries.length === 0) {
        setDateRangeError('No timesheet exists for the selected dates');
        return;
      }

      generateTimesheetPDF({
        employeeName: employee.name,
        clientName: employee.client_name,
        jobTitle: employee.job_title,
        month: fromDate.month() + 1,
        year: fromDate.year(),
        status: dateRangeTarget.status,
        expectedDailyHours: Number(employee.expected_daily_hours || 8),
        totalHours: 0,
        entries,
        dateRange: { from: fromStr, to: toStr },
        isIndirect: dateRangeTarget.is_indirect,
      });

      setDateRangeTarget(null);
    } catch (err: any) {
      setDateRangeError(err.response?.data?.error || 'Failed to download timesheet');
    } finally {
      setDateRangeLoading(false);
    }
  };

  // Date range action — routes to download or email based on mode
  const handleDateRangeAction = async () => {
    if (dateRangeEmailMode) {
      // For email mode, close date range dialog and open email confirmation
      if (!dateRangeTarget || !fromDate || !toDate) return;
      const fromStr = fromDate.format('YYYY-MM-DD');
      const toStr = toDate.format('YYYY-MM-DD');
      setPendingEmailDateRange({ from: fromStr, to: toStr });
      setPendingEmailFilter(undefined);
      setDateRangeTarget(null);
      setEmailConfirmOpen(true);
    } else {
      await handleDateRangeDownload();
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];
  const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getStatusChip = (status: string) => {
    const label = statusDisplayLabels[status] || status;
    switch (status) {
      case 'draft':
        return <Chip label={label} size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }} />;
      case 'submitted':
        return <Chip label={label} size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 500, fontSize: 11, height: 24 }} />;
      case 'approved':
        return <Chip label={label} size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }} />;
      case 'rejected':
        return <Chip label={label} size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 500, fontSize: 11, height: 24 }} />;
      default:
        return <Chip label={label} size="small" sx={{ backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 500, fontSize: 11, height: 24 }} />;
    }
  };

  const SortHeader = ({ field, label, width, align }: { field: SortField; label: string; width?: number | string; align?: 'left' | 'center' | 'right' }) => (
    <TableCell sx={{ fontWeight: 600, width }} align={align}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Timesheets</Typography>
          <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.25 }}>Review and manage employee timesheets</Typography>
        </Box>
        {!loading && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{
              px: 2, py: 1.25, bgcolor: '#F0FFF4', borderRadius: 2,
              border: '1px solid #BBF7D0', textAlign: 'right', minWidth: 180,
            }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#15803D', lineHeight: 1.2 }}>
                ${timesheets.reduce((sum, ts) => {
                  const margin = (Number(ts.hourly_rate) || 0) - (Number(ts.kovil_cost) || 0);
                  return sum + margin * (Number(ts.total_hours) || 0);
                }, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#15803D', fontWeight: 500, opacity: 0.8, mt: 0.25 }}>
                Total Gross Profit Margin (MTD)
              </Typography>
            </Box>
            <Box sx={{
              px: 2, py: 1.25, bgcolor: '#F5F3FF', borderRadius: 2,
              border: '1px solid #DDD6FE', textAlign: 'right', minWidth: 160,
            }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#7C3AED', lineHeight: 1.2 }}>
                ${timesheets.reduce((sum, ts) => sum + Number(ts.total_hours || 0) * Number(ts.hourly_rate || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#7C3AED', fontWeight: 500, opacity: 0.8, mt: 0.25 }}>
                {MONTHS[month - 1]} {year} Revenue
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Filters Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Month Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
          <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 140, textAlign: 'center' }}>
            {MONTHS[month - 1]} {year}
          </Typography>
          <IconButton size="small" onClick={nextMonth}><ChevronRight /></IconButton>
        </Box>

        {/* Total Billable Hours */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: 1, border: '1px solid #BBF7D0' }}>
          <Typography variant="subtitle2" fontWeight={700} color="#16A34A" fontSize={18}>
            {formatHoursMinutes(timesheets.reduce((sum, ts) => sum + Number(ts.total_hours || 0), 0))}
          </Typography>
          <Typography variant="caption" color="#15803D" fontWeight={500}>
            Total {MONTHS[month - 1]} Billable Hours
          </Typography>
        </Box>

        {/* Client filter */}
        <TextField
          select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value as unknown as string[])}
          size="small"
          sx={{ minWidth: 180 }}
          label="Client"
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return 'All Clients';
              if (sel.length === 1) return clients.find(c => c.id === sel[0])?.client_name ?? sel[0];
              return `${sel.length} clients`;
            },
          }}
        >
          {(() => {
            const activeIds = clients.filter(c => c.status === 'active' && !c.is_test).map(c => c.id);
            return [
              <MenuItem
                key="__select_all__"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setClientFilter(clientFilter.length === activeIds.length ? [] : activeIds);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    checked={activeIds.length > 0 && clientFilter.length === activeIds.length}
                    indeterminate={clientFilter.length > 0 && clientFilter.length < activeIds.length}
                    size="small"
                  />
                </ListItemIcon>
                Select All
              </MenuItem>,
              <Divider key="__divider__" />,
              ...clients.filter(c => c.status === 'active' && !c.is_test).map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon sx={{ minWidth: 32 }}><Checkbox checked={clientFilter.includes(c.id)} size="small" /></ListItemIcon>
                  {c.client_name}
                </MenuItem>
              )),
            ];
          })()}
        </TextField>

        {/* Search */}
        <TextField
          placeholder="Search employee, UID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94A3B8' }} /></InputAdornment>,
          }}
        />
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
            All Timesheets
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
            {filtered.length} timesheet{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1300 }}>
          <TableHead>
            <TableRow>
              <SortHeader field="employee_name" label="Employee" width="17%" />
              <SortHeader field="client_name" label="Client" width="12%" />
              <SortHeader field="status" label="Status" width="8%" />
              <SortHeader field="hourly_rate" label="Client Rate ($/hr)" width="7%" align="right" />
              <TableCell sx={{ fontWeight: 600, width: '7%', textAlign: 'right' }}>Kovil Cost ($/hr)</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '8%', textAlign: 'right' }}>Gross Margin ($/hr)</TableCell>
              <SortHeader field="total_hours" label="Total Hours" width="6%" align="right" />
              <TableCell sx={{ fontWeight: 600, width: '8%', textAlign: 'right' }}>Revenue</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'right' }}>Gross Profit Margin</TableCell>
              <SortHeader field="updated_at" label="Last Updated" width="8%" />
              <TableCell sx={{ fontWeight: 600, width: '9%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {timesheets.length === 0
                    ? `No timesheets found for ${MONTHS[month - 1]} ${year}`
                    : 'No timesheets match filters'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((ts) => (
                <TableRow
                  key={ts.id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    ...(ts.status === 'submitted' ? { backgroundColor: '#fffde7' } : {}),
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        backgroundColor: `${getAvatarColor(ts.employee_name)}15`,
                        color: getAvatarColor(ts.employee_name),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(ts.employee_name)}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                          {ts.employee_name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94A3B8', fontSize: 11 }}>
                          {ts.employee_uid}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={13}>{ts.client_name}</Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(ts.status)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontSize={13} color="text.secondary">
                      ${Number(ts.hourly_rate || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontSize={13} color="text.secondary">
                      {ts.kovil_cost != null ? `$${Number(ts.kovil_cost)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      const rate = Number(ts.hourly_rate) || 0;
                      const cost = Number(ts.kovil_cost) || 0;
                      if (!ts.hourly_rate && !ts.kovil_cost) return <Typography variant="body2" fontSize={13} color="text.secondary">—</Typography>;
                      const margin = rate - cost;
                      const pct = rate > 0 ? Math.round((margin / rate) * 100) : null;
                      return (
                        <Typography variant="body2" fontSize={13} fontWeight={600}
                          sx={{ color: margin >= 0 ? '#15803D' : '#DC2626' }}>
                          ${margin.toFixed(2)}{pct !== null ? ` (${pct}%)` : ''}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500} fontSize={13}>
                      {formatHoursMinutes(Number(ts.total_hours || 0))}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {Number(ts.total_hours || 0) > 0 ? (
                      <Typography variant="body2" fontWeight={600} fontSize={13} sx={{ color: '#059669' }}>
                        ${(Number(ts.total_hours || 0) * Number(ts.hourly_rate || 0)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontSize={13} color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      const hours = Number(ts.total_hours) || 0;
                      const margin = (Number(ts.hourly_rate) || 0) - (Number(ts.kovil_cost) || 0);
                      if (hours === 0 || (!ts.hourly_rate && !ts.kovil_cost)) {
                        return <Typography variant="body2" fontSize={13} color="text.disabled">—</Typography>;
                      }
                      const profit = margin * hours;
                      return (
                        <Typography variant="body2" fontWeight={600} fontSize={13}
                          sx={{ color: profit >= 0 ? '#15803D' : '#DC2626' }}>
                          ${profit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={12} color="text.secondary">
                      {Number(ts.total_hours || 0) === 0 && ts.status === 'draft'
                        ? '—'
                        : formatRelativeDate(ts.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => handleView(ts)} title="View Details"
                        sx={{ color: '#94A3B8', '&:hover': { color: '#3B82F6', backgroundColor: '#EFF6FF' } }}>
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDateRange(ts)} title="Download PDF"
                        sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}>
                        <PictureAsPdf sx={{ fontSize: 18 }} />
                      </IconButton>
                      {ts.status === 'submitted' && (
                        <>
                          <IconButton size="small" onClick={() => setApproveTarget(ts)} title="Approve"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#10B981', backgroundColor: '#D1FAE5' } }}>
                            <CheckCircle sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setRejectTarget(ts)} title="Reject"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}>
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* View Details Dialog */}
      <Dialog
        open={!!viewTs}
        onClose={handleCloseView}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        {viewTs && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="span" fontWeight={600}>
                  {viewTs.employee_name}
                </Typography>
                <Typography variant="h6" component="span" fontWeight={400} color="text.secondary">
                  {' '}&mdash; {MONTHS[viewTs.month - 1]} {viewTs.year}
                </Typography>
              </Box>
              {getStatusChip(viewTs.status)}
            </DialogTitle>
            <Box sx={{ px: 3, pb: 1.5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Employee:</strong> {viewTs.employee_name} ({viewTs.employee_uid}){viewTs.job_title ? ` — ${viewTs.job_title}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Client:</strong> {viewTs.client_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Expected Daily Hrs:</strong> {viewTs.expected_daily_hours || 8}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Total Hours:</strong> {formatHoursMinutes(Number(viewTs.total_hours || 0))}
              </Typography>
            </Box>
            <DialogContent sx={{ pt: 0 }}>
              {loadingEntries ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TimesheetGrid
                  entries={viewEntries}
                  editable={false}
                  expectedDailyHours={viewTs.expected_daily_hours || 8}
                  onEntryUpdate={() => {}}
                  gridHeight="calc(85vh - 220px)"
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              {/* Download PDF button with dropdown */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                startIcon={<PictureAsPdf />}
                endIcon={<ArrowDropDown />}
                disabled={loadingEntries || viewEntries.length === 0}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Download PDF
              </Button>

              {/* Email Timesheet button with dropdown — hidden for indirect clients */}
              {!viewTs.is_indirect && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => setEmailMenuAnchor(e.currentTarget)}
                  startIcon={<Email />}
                  endIcon={<ArrowDropDown />}
                  disabled={loadingEntries || viewEntries.length === 0 || emailSending}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, mr: 'auto' }}
                >
                  {emailSending ? 'Sending...' : 'Email Timesheet'}
                </Button>
              )}
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { minWidth: 200, mb: 0.5 } }}
              >
                <MenuItem onClick={() => handleDownloadPDF()} sx={{ py: 0.75 }}>
                  <ListItemText
                    primary={`${MONTH_ABBR[viewTs.month - 1]} ${viewTs.year} — Full Month`}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setDownloadMenuAnchor(null); handleOpenDateRange(viewTs, false); }} sx={{ py: 0.75 }}>
                  <ListItemText
                    primary="Custom Dates"
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                  />
                </MenuItem>
              </Menu>
              {!viewTs.is_indirect && (
                <Menu
                  anchorEl={emailMenuAnchor}
                  open={Boolean(emailMenuAnchor)}
                  onClose={() => setEmailMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  PaperProps={{ sx: { minWidth: 200, mb: 0.5 } }}
                >
                  <MenuItem onClick={() => handleEmailMenuSelect()} sx={{ py: 0.75 }}>
                    <ListItemText
                      primary={`${MONTH_ABBR[viewTs.month - 1]} ${viewTs.year} — Full Month`}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { setEmailMenuAnchor(null); handleOpenDateRange(viewTs, true); }} sx={{ py: 0.75 }}>
                    <ListItemText
                      primary="Custom Dates"
                      primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                    />
                  </MenuItem>
                </Menu>
              )}

              {viewTs.status === 'submitted' && (
                <>
                  <Button
                    onClick={() => { setRejectTarget(viewTs); }}
                    color="error"
                    variant="outlined"
                    size="small"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => { setApproveTarget(viewTs); }}
                    color="success"
                    variant="contained"
                    size="small"
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button onClick={handleCloseView} color="inherit" size="small">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approve Confirmation */}
      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Approve Timesheet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Approve timesheet for <strong>{approveTarget?.employee_name}</strong> &mdash; {approveTarget ? `${MONTHS[approveTarget.month - 1]} ${approveTarget.year}` : ''}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setApproveTarget(null)} color="inherit" disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleApprove} color="success" variant="contained" disabled={actionLoading}>
            {actionLoading ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation */}
      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Reject Timesheet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reject timesheet for <strong>{rejectTarget?.employee_name}</strong> &mdash; {rejectTarget ? `${MONTHS[rejectTarget.month - 1]} ${rejectTarget.year}` : ''}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRejectTarget(null)} color="inherit" disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Range Dialog (shared for download + email) */}
      <Dialog
        open={!!dateRangeTarget}
        onClose={() => !dateRangeLoading && setDateRangeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {dateRangeEmailMode ? 'Email Timesheet' : 'Download Timesheet'}
        </DialogTitle>
        <DialogContent>
          {dateRangeTarget && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                {dateRangeTarget.employee_name} ({dateRangeTarget.employee_uid})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2.5, display: 'block' }}>
                Select a date range (max 3 months)
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(v) => { setFromDate(v); setDateRangeError(null); }}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(v) => { setToDate(v); setDateRangeError(null); }}
                    format="DD/MM/YYYY"
                    minDate={fromDate || undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
              </LocalizationProvider>

              {dateRangeTooLong && (
                <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
                  Date range exceeds 3 months. Please select a shorter range.
                </Alert>
              )}

              {dateRangeError && (
                <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
                  {dateRangeError}
                </Alert>
              )}

              {dateRangeFetching && !dateRangeError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Checking entries...</Typography>
                </Box>
              )}

              {dateRangeValidation && !dateRangeFetching && !dateRangeError && (
                <Alert severity={dateRangeValidation.hasIssues ? 'warning' : 'success'} sx={{ mt: 2, fontSize: 13 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                    Date range: {dateRangeValidation.header}
                  </Typography>
                  {dateRangeValidation.missingTasks.length > 0 && (
                    <Typography variant="body2" sx={{ fontSize: 12, mt: 0.75 }}>
                      <strong>Task Description missing for:</strong>{' '}
                      {dateRangeValidation.missingTasks.join(' \u00b7 ')}
                    </Typography>
                  )}
                  {dateRangeValidation.missingHours.length > 0 && (
                    <Typography variant="body2" sx={{ fontSize: 12, mt: 0.5 }}>
                      <strong>Time not updated for:</strong>{' '}
                      {dateRangeValidation.missingHours.join(' \u00b7 ')}
                    </Typography>
                  )}
                  {dateRangeValidation.hasIssues && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                      If it's leave/not worked on a particular day, select 0:00 to 0:00 hours
                    </Typography>
                  )}
                  {!dateRangeValidation.hasIssues && (
                    <Typography variant="body2" sx={{ fontSize: 12, mt: 0.25 }}>
                      All weekday entries have task details and hours logged.
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDateRangeTarget(null)}
            color="inherit"
            disabled={dateRangeLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDateRangeAction}
            variant="contained"
            color={dateRangeEmailMode ? 'success' : 'primary'}
            disabled={!dateRangeValid || dateRangeLoading}
            startIcon={dateRangeLoading ? <CircularProgress size={16} /> : (dateRangeEmailMode ? <Email /> : <PictureAsPdf />)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {dateRangeLoading
              ? (dateRangeEmailMode ? 'Preparing...' : 'Downloading...')
              : (dateRangeEmailMode ? 'Send Email' : 'Download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Confirmation Dialog */}
      <Dialog open={emailConfirmOpen} onClose={() => setEmailConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Email Timesheet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to email the <strong>{getEmailFilterLabel()}</strong> timesheet for <strong>{viewTs?.employee_name}</strong> to the client for approval?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEmailConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleEmailTimesheet} variant="contained" color="success" autoFocus>
            Yes, Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Snackbars */}
      <Snackbar open={emailStatus === 'sent'} autoHideDuration={4000} onClose={() => setEmailStatus('idle')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" variant="filled" onClose={() => setEmailStatus('idle')}>Timesheet emailed successfully!</Alert>
      </Snackbar>
      <Snackbar open={emailStatus === 'error'} autoHideDuration={6000} onClose={() => setEmailStatus('idle')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" variant="filled" onClose={() => setEmailStatus('idle')}>{emailError}</Alert>
      </Snackbar>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
